// Single source of truth for lead-status display — colors, the effective-
// status priority chain, and the Under Review per-lead badge. Mirrors the
// backend's app/utils/lead_status.py. This module used to be duplicated
// (colors 5x across Goals.js/Programs.js/Components.js/SupplierPortal.js/
// LeadQA.js, the priority chain 3x within LeadQA.js alone) and that's
// exactly how the rollup and the tabs drifted out of sync with each other
// over several rounds of patching. Import from here — do not re-derive.

// The 5 lifecycle statuses. Lead.status is the single source of truth for
// which bucket a lead is in — everywhere (rollups, tabs, program-level
// views) except Lead QA Review's own table, which deliberately hides
// Pending Review + not-yet-delivered leads (see backend CLAUDE.md).
export const LEAD_STATUSES = [
  'Pending Review',
  'Under Review',
  'DemandArc Accepted',
  'Client Accepted',
  'Rejected',
];

// A 6th status, "Approved Pending Call Review", sits between Under Review and
// DemandArc Accepted for any component with Component.includes_call_review = true
// — call getLeadStatuses(includesCallReview) instead of using LEAD_STATUSES
// directly wherever the relevant component's flag (or a union across several) is
// known, so the extra status gets spliced into the right position in exactly one
// place rather than every consumer (tabs, rollup columns, color legends)
// re-deciding it independently.
export function getLeadStatuses(includesCallReview) {
  if (!includesCallReview) return LEAD_STATUSES;
  return [
    'Pending Review',
    'Under Review',
    'Approved Pending Call Review',
    'DemandArc Accepted',
    'Client Accepted',
    'Rejected',
  ];
}

export const LEAD_STATUS_COLORS = {
  'Pending Review':               { bg: '#fef9e7', color: '#b7770d', border: '#f9e79f' },
  'Under Review':                 { bg: '#eaf0fb', color: '#1a6da8', border: '#aed6f1' },
  'Approved Pending Call Review': { bg: '#f4ecfb', color: '#7d3c98', border: '#d7bde2' },
  'DemandArc Accepted':           { bg: '#e8f8f5', color: '#0e6655', border: '#a3e4d7' },
  'Client Accepted':              { bg: '#eafaf1', color: '#1e8449', border: '#a9dfbf' },
  'Rejected':                     { bg: '#fdedec', color: '#c0392b', border: '#f5b7b1' },
};

// Per-lead badge colors shown only within the "Under Review" bucket —
// informational, never a bucket/tab of their own.
const UNDER_REVIEW_BADGE_COLORS = {
  'Not Run':         { bg: '#f5f5f5', color: '#888',    border: '#ddd' },
  'Needs Review':    { bg: '#fef9e7', color: '#b7950b', border: '#f9e79f' },
  // A lead a PM parked on purpose (override "Needs Review – Revisit Later"). A
  // different label from the untouched "Needs Review" so the QA Status filter can
  // tell them apart; lead.status stays Under Review either way.
  'Revisit Later':   { bg: '#eef2fb', color: '#3b5bb5', border: '#c5d0ee' },
  'Client Returned': { bg: '#fef0e6', color: '#d35400', border: '#f5cba7' },
};

// Only these effective statuses have a corresponding terminal Lead.status to
// promote into — mirrors leads.py's EFFECTIVE_TO_TERMINAL_STATUS exactly.
const EFFECTIVE_TO_TERMINAL_STATUS = {
  Accepted:          'DemandArc Accepted',
  Rejected:           'Rejected',
  'Client Accepted': 'Client Accepted',
};

// qa_override -> status -> qa_status priority chain. Does NOT decide bucket
// membership (that's lead.status alone) — only used for the Under Review
// badge below and as the promotion-target preview for "Accept Selected QA
// Statuses". Mirrors backend's effective_status() exactly.
export function effectiveStatus(lead) {
  if (lead.qa_override === 'Accept') return 'Accepted';
  if (lead.qa_override === 'Reject') return 'Rejected';
  if (lead.qa_override === 'Client Rejected') return 'Rejected'; // "by Client" vs "by DA" — same effective/terminal status, see rejectedStatusLabel() in LeadQA.js for the display split
  if (lead.qa_override === 'Client Accepted') return 'Client Accepted';
  if (lead.qa_override === 'Client Returned') return 'Client Returned';
  if (lead.qa_override === 'Needs Review') return 'Needs Review';
  if (lead.status === 'Client Accepted') return 'Client Accepted';
  if (lead.status === 'Rejected') return 'Rejected';
  return lead.qa_status || null;
}

// Badge for a lead currently sitting in "Under Review": Not Run / Needs
// Review / Client Returned / "Ready -> <target>" (colored to match the
// target status it would promote into). Only meaningful when
// lead.status === 'Under Review' — other statuses are already
// terminal/pre-accept and self-explanatory from their lifecycle status alone.
// includesCallReview is the lead's own component's flag — when true, an
// "Accepted" lead previews "Ready → Approved Pending Call Review" instead of
// "Ready → DemandArc Accepted", matching what POST /leads/accept-qa-statuses
// actually does for a still-Under-Review lead (mirrors leads.py exactly).
export function underReviewBadge(lead, includesCallReview) {
  if (lead.qa_override === 'Client Returned') {
    return { label: 'Client Returned', ...UNDER_REVIEW_BADGE_COLORS['Client Returned'] };
  }
  if (lead.qa_override === 'Needs Review') {
    return { label: 'Revisit Later', ...UNDER_REVIEW_BADGE_COLORS['Revisit Later'] };
  }
  const eff = effectiveStatus(lead);
  if (eff === 'Needs Review') {
    return { label: 'Needs Review', ...UNDER_REVIEW_BADGE_COLORS['Needs Review'] };
  }
  let target = EFFECTIVE_TO_TERMINAL_STATUS[eff];
  if (target === 'DemandArc Accepted' && includesCallReview && lead.status === 'Under Review') {
    target = 'Approved Pending Call Review';
  }
  if (target) {
    return { label: `Ready → ${target}`, ...LEAD_STATUS_COLORS[target] };
  }
  return { label: 'Not Run', ...UNDER_REVIEW_BADGE_COLORS['Not Run'] };
}
